import React, { FC, useState } from 'react';
import { Checkbox, Modal, Tooltip } from 'antd';
import { Close, WarningCircle } from 'common/svg';
import { Selector } from 'common/components/Selector/Selector';
import { priceFormatter } from 'common/helpers/format.helper';
import { IMonitorSalesProductItem } from 'entities/MonitorSales/MonitorSales.models';
import { useProductSalesTreeMap } from 'entities/MonitorSales/useProductSalesTreeMap';

const CATEGORY_COLOR_VISIBILITY_OPTIONS = [
  { value: 'false', label: 'Hide Categories' },
  { value: 'true', label: 'Show Categories' },
];

interface IComponentProps {
  open: boolean;
  products: IMonitorSalesProductItem[];
  onCancel: () => void;
}

export const ProductSalesModal: FC<IComponentProps> = ({ open, products, onCancel }) => {
  const [showCategoryColors, setShowCategoryColors] = useState<boolean>(false);
  const [groupSegments, setGroupSegments] = useState<boolean>(false);
  const { containerRef, canvasWidth, canvasHeight, cells, groups } = useProductSalesTreeMap(
    products,
    open,
    showCategoryColors,
    groupSegments
  );

  const handleFilterChange = (filter?: string | number): void => {
    setShowCategoryColors(filter === 'true');
  };

  return (
    <Modal
      open={open}
      rootClassName="modal ms-products__modal"
      width={1152}
      centered
      closeIcon={<Close className="icon-gray" />}
      onCancel={onCancel}
      footer={null}
      title={
        <div className="ms-products__modal-title">
          <span className="mr-2 ff-medium fs-xs">Product Sales (Top-10)</span>
          <WarningCircle />
        </div>
      }
    >
      <div className="mb-6 flex flex-align-center">
        <div>
          <Selector
            allowClear={false}
            options={CATEGORY_COLOR_VISIBILITY_OPTIONS}
            value={String(showCategoryColors)}
            onChange={handleFilterChange}
          />
        </div>

        {showCategoryColors && (
          <Checkbox
            className="ml-5 ff-regular"
            checked={groupSegments}
            onChange={(e) => setGroupSegments(e.target.checked)}
          >
            Group segments
          </Checkbox>
        )}
      </div>

      <div ref={containerRef} className="ms-products__treemap">
        <div className="ms-products__treemap-canvas" style={{ width: canvasWidth, height: canvasHeight }}>
          {groups.map((group) => (
            <div
              key={group.key}
              className="ms-products__treemap-group-header"
              style={{ left: group.x, top: group.y, width: group.width }}
            >
              {group.category} — {group.total.toFixed(1)}%
            </div>
          ))}

          {cells.map((cell) => (
            <Tooltip
              key={cell.key}
              destroyTooltipOnHide={true}
              title={
                <div className="flex flex__column">
                  <span className="ff-medium">{cell.item.name}</span>
                  <span className="ff-regular fs-xxxs color-gray-1">{cell.item.category}</span>
                  <span className="ff-regular fs-xxxs">{`${priceFormatter(cell.item.sales)} (${
                    cell.item.salesPercent
                  }%)`}</span>
                </div>
              }
            >
              <div
                className="ms-products__treemap-cell"
                style={{
                  left: cell.x,
                  top: cell.y,
                  width: cell.width,
                  height: cell.height,
                  background: cell.background,
                  color: cell.color,
                }}
              >
                <span className="ms-products__treemap-cell-name">{cell.item.name}</span>
                <span className="ms-products__treemap-cell-pct">{cell.item.salesPercent}%</span>
              </div>
            </Tooltip>
          ))}
        </div>
      </div>
    </Modal>
  );
};
